"use client";

import React, { useRef, useState } from "react";
import {
  Badge,
  Container,
  Dialog,
  Logo,
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  SearchBox,
} from "@repo/ui";
import { ShoppingCartOutlined } from "@spoctech/icons";
import capitalize from "lodash/capitalize";
import Link from "next/link";
import Icon from "./Icon";
import useRouteChange from "@/hooks/useRouteChange";
import { useAppSelector } from "@/store/hooks";
import { Menu, X } from "lucide-react";
import isEmpty from "lodash/isEmpty";

import { useCountryCode } from "@/hooks/useCountryCode";
import dynamic from "next/dynamic";
const MegaMenu = dynamic(() => import("./common/MegaMenu"));
const CountryList = dynamic(() => import("./common/CountryList"));
// import Loading from "./common/Loading";

const resourceList = [
  {
    title: "Blog",
    href: "/blog/",
    description: "Latest insights and trends in education",
  },
  // {
  //   title: "Case Study",
  //   href: "/case-studies/",
  //   description: "Real-world success stories and implementations",
  // },
  {
    title: "News",
    href: "/news/",
    description: "Stay updated with industry news and announcements",
  },
  // {
  //   title: "Webinar",
  //   href: "/webinar/",
  //   description: "Join our live sessions with industry experts",
  // },
];

const Header = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [openCountryDialog, setOpenCountryDialog] = useState(false);
  const [navigationValue, setNavigationValue] = useState(null);
  const cartItem = useAppSelector((state) => state.cart.cartItem);
  const { country, paramsHasCountry } = useCountryCode();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useRouteChange({
    onChangeComplete: () => {
      setOpenCountryDialog(false);
      setMobileMenuOpen(false);
      setNavigationValue(null);
    },
  });

  return (
    <header className="bg-white shadow-md fixed top-0 left-0 right-0 z-[999] min-h-16 flex items-center">
      <Container className="relative" ref={containerRef}>
        <div className="flex items-center gap-1.5 sm:gap-3 py-3 justify-center">
          {/* Logo */}
          <Link
            href={paramsHasCountry ? `/${country}` : "/"}
            className="mr-auto"
          >
            <Logo
              title="Spoclearn"
              className="md:w-[175px] sm:w-[165px] w-[150px]"
            />
          </Link>
          {/* Desktop Navigation */}

          <NavigationMenu
            container={containerRef}
            className="hidden md:flex flex-1 items-center justify-between"
            value={navigationValue}
            onValueChange={setNavigationValue}
          >
            <NavigationMenuList className="gap-2 ml-auto">
              {/* Megamenu */}

              {/* <Suspense
                fallback={
                  <Button
                    className="bg-gray-100"
                    endIcon={<ChevronDownIcon size={18} />}
                  >
                    View Courses
                  </Button>
                }
              > */}
              <MegaMenu />
              {/* </Suspense> */}

              <NavigationMenuItem>
                <SearchBox />
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuLink
                  render={<Link href="/corporate-training/" />}
                >
                  Corporate
                </NavigationMenuLink>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuLink render={<Link href="/consulting/" />}>
                  Consulting
                </NavigationMenuLink>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuTrigger>Resource</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul>
                    {resourceList.map((resource) => (
                      <li key={resource.href}>
                        <NavigationMenuLink
                          render={
                            <Link
                              href={resource.href}
                              className="block p-2 hover:bg-gray-50 rounded-md"
                            />
                          }
                        >
                          <div className="font-medium">{resource.title}</div>
                          <p className="text-sm text-gray-500">
                            {resource.description}
                          </p>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
          {/* Right Icons (always visible, but adjust for mobile) */}
          <div className="flex items-center gap-3 md:gap-4 ml-auto">
            {/* Country Selector */}
            <button
              onClick={() => setOpenCountryDialog(true)}
              title="Country list"
              className="flex items-center gap-2 hover:text-primary-light text-sm bg-gray-200 px-2 py-1 rounded-lg"
            >
              Location
              <Icon
                name={country ? capitalize(country) : "Us"}
                width={18}
                className="rounded-sm"
              />
            </button>
            {/* Cart Icon */}
            {!isEmpty(cartItem) && (
              <Badge
                badgeContent={1}
                color="primary"
                render={
                  <Link
                    title="order summary"
                    href={`/${country}/order-summary/`}
                    className="hover:text-primary-light"
                  />
                }
              >
                <ShoppingCartOutlined />
              </Badge>
            )}
            {/* Profile Icon */}
            {/* <NavigationMenu render={<div />} className="hidden sm:block">
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger title="Profile" className="xs:!px-1">
                    <UserOutlined width={18} />
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul>
                      <li>
                        <NavigationMenuLink href="#">
                          Profile
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink href="#">
                          Refer & Earn
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink href="#">Logout</NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu> */}
          </div>
          {/* Hamburger for Mobile */}
          <button
            className="md:hidden ml-2"
            onClick={() => setMobileMenuOpen(true)}
            title="Menu toggle"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
        {/* Mobile Drawer/Sidebar */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[1000] flex md:hidden">
            <div className="bg-white w-4/5 max-w-xs h-full px-6 py-[1.1rem] flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <Logo title="Spoclearn" width={175} />
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  title="Menu toggle"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <NavigationMenu orientation="vertical">
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuLink
                      className="!justify-start"
                      render={<Link href="/courses/" />}
                    >
                      Explore Courses
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuLink
                      className="!justify-start"
                      render={<Link href="/corporate-training" />}
                    >
                      Corporate Training
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  {/* <NavigationMenuItem>
                    <NavigationMenuLink
                      className="!justify-start"
                      render={<Link href="/webinar/" />}
                    >
                      Webinar
                    </NavigationMenuLink>
                  </NavigationMenuItem> */}
                  <NavigationMenuItem>
                    <NavigationMenuLink
                      className="!justify-start"
                      render={<Link href="/one-to-one-training/" />}
                    >
                      1 to 1 Training
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  <NavigationMenuItem>
                    <NavigationMenuLink
                      className="!justify-start"
                      render={<Link href="/contact-us/" />}
                    >
                      Contact us
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>
            {/* Click outside to close */}
            <div className="flex-1" onClick={() => setMobileMenuOpen(false)} />
          </div>
        )}
      </Container>

      {/* Country list dialog */}
      <Dialog
        title="See Spoclearn.com in your country/region"
        open={openCountryDialog}
        onOpenChange={(mState: boolean) => setOpenCountryDialog(mState)}
        maxWidth="lg"
      >
        {/* CountryList */}
        {/* <Suspense fallback={<Loading message="Country loading...." />}> */}
        <CountryList />
        {/* </Suspense> */}
      </Dialog>
    </header>
  );
};

export default Header;
